import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '@/lib/api';

const isConfiguredApiUrl = !!API_URL;
const CSRF_COOKIE_NAME = '__Secure-csrf_token';
const isValidUserPayload = (value) => {
  return !!value && typeof value === 'object' && typeof value.user_id === 'string' && value.user_id.length > 0;
};

// Global axios config: always send cookies
axios.defaults.withCredentials = true;

// Helper to read a cookie by name
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// CSRF interceptor: attach the shared cross-subdomain CSRF cookie value as X-CSRF-Token.
axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'delete', 'patch'].includes(method)) {
    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminTabs, setAdminTabs] = useState([]);
  const [canReport, setCanReport] = useState(false);

  const ensureCsrfCookie = useCallback(async () => {
    if (getCookie(CSRF_COOKIE_NAME)) return;
    try {
      await axios.get(`${API_URL}/api/settings/public`);
    } catch (error) {
      // Any GET response is enough for the middleware to seed a CSRF cookie.
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const [tabsRes, reportRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/my-permissions`).catch(() => ({ data: { tabs: [] } })),
        axios.get(`${API_URL}/api/users/me/can-report`).catch(() => ({ data: { can_report: false } }))
      ]);
      setAdminTabs(tabsRes.data.tabs || []);
      setCanReport(reportRes.data.can_report || false);
    } catch (e) {
      setAdminTabs([]);
      setCanReport(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (!isConfiguredApiUrl) {
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
        console.error('REACT_APP_BACKEND_URL is missing in this build. Auth requests cannot be resolved correctly.');
      }
      setUser(null);
      setAdminTabs([]);
      setCanReport(false);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      if (!isValidUserPayload(response.data)) {
        throw new Error('Received a non-user auth payload');
      }
      setUser(response.data);
      await fetchPermissions();
    } catch (error) {
      setUser(null);
      setAdminTabs([]);
      setCanReport(false);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    await ensureCsrfCookie();
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    await fetchPermissions();
    return userData;
  };

  const register = async (username, email, password) => {
    await ensureCsrfCookie();
    const response = await axios.post(`${API_URL}/api/auth/register`, { username, email, password });
    return response.data;
  };

  const loginWithGoogleCredential = async (credential) => {
    await ensureCsrfCookie();
    const response = await axios.post(`${API_URL}/api/auth/google`, { credential });
    const { user: userData } = response.data;
    setUser(userData);
    await fetchPermissions();
    return userData;
  };

  const loginWithFacebook = async (accessToken) => {
    await ensureCsrfCookie();
    const response = await axios.post(`${API_URL}/api/auth/facebook/callback`, { accessToken });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setAdminTabs([]);
    setCanReport(false);
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const profileComplete = !user || (!!user.display_name && !user.must_change_display_name);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      loginWithGoogleCredential,
      loginWithFacebook,
      logout,
      updateUser,
      isAdmin,
      adminTabs,
      canReport,
      checkAuth,
      profileComplete
    }}>
      {children}
    </AuthContext.Provider>
  );
};
